import {
  BASE_CATEGORY_COLORS,
  EXPENSE_CATEGORY_LABELS,
  categoryCatalog,
  getActiveCategories,
  type BaseExpenseCategory,
  type CategoryDef,
  type ExpenseCategory,
} from "@torre/expenses";

// Formato y etiquetas de la app Gastos y Presupuestos.

export const lang = (language: string | undefined): "es" | "en" => (String(language || "es").startsWith("en") ? "en" : "es");

export function money(value: number | null | undefined, currency = "MXN", language = "es"): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat(lang(language) === "en" ? "en-US" : "es-MX", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(value);
}

// --- Categorías (#1255) ---------------------------------------------------------
// Nombre, color y opciones salen del catálogo de la COMPAÑÍA (las del sistema con
// sus ajustes y las propias), que `useCategoryCatalog` deja como catálogo activo
// al abrir la app. Mientras no carga, valen las del sistema.

const SYSTEM_CATALOG = categoryCatalog([]);
const FALLBACK_COLOR = "#94a3b8";

/** Catálogo vigente: el de la compañía si ya cargó; si no, las del sistema. */
export const currentCategories = (): readonly CategoryDef[] => (getActiveCategories() as readonly CategoryDef[] | null) ?? SYSTEM_CATALOG;

const findCategory = (id: string) => currentCategories().find((c) => c.id === id);

/** Ids de todas las categorías del catálogo vigente (incluidas las archivadas), en su orden. */
export const categoryIdList = (): string[] => currentCategories().map((c) => c.id);

export const categoryLabel = (c: string, language = "es") =>
  findCategory(c)?.label?.[lang(language)] ?? EXPENSE_CATEGORY_LABELS[c as BaseExpenseCategory]?.[lang(language)] ?? c;

/**
 * Opciones de categoría. Para CAPTURAR (por omisión) solo las activas;
 * `include` agrega la del registro que se edita aunque esté archivada.
 * Para FILTRAR la historia, `archived: true` las trae todas.
 */
export const categoryOptions = (language = "es", options: { include?: string | null; archived?: boolean } = {}) =>
  currentCategories()
    .filter((c) => options.archived || !c.archived || c.id === options.include)
    .map((c) => ({
      value: c.id,
      label: c.archived ? `${categoryLabel(c.id, language)} (${lang(language) === "en" ? "archived" : "archivada"})` : categoryLabel(c.id, language),
    }));

/** Color de una categoría (mismo color en tabla, gráfica y chips). */
export const categoryColor = (c: string): string => findCategory(c)?.color ?? BASE_CATEGORY_COLORS[c as BaseExpenseCategory] ?? FALLBACK_COLOR;

/**
 * Colores por categoría, como objeto: `CATEGORY_COLORS[id]` resuelve contra el
 * catálogo vigente (incluye las categorías propias), sin cambiar a quien ya lo
 * indexaba cuando era una tabla fija.
 */
export const CATEGORY_COLORS: Record<ExpenseCategory, string> = new Proxy({} as Record<string, string>, {
  get: (_target, key) => (typeof key === "string" ? categoryColor(key) : undefined),
  has: (_target, key) => typeof key === "string" && !!findCategory(key),
  ownKeys: () => currentCategories().map((c) => c.id),
  getOwnPropertyDescriptor: (_target, key) => (typeof key === "string" && findCategory(key) ? { enumerable: true, configurable: true, value: categoryColor(key) } : undefined),
});

/** Fecha y hora local (México) en el idioma de la interfaz. */
export function dateTime(iso: string | null | undefined, language = "es"): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(lang(language) === "en" ? "en-US" : "es-MX", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "America/Mexico_City",
  });
}

const MONTHS_ES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const MONTHS_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-09" → "sep 2026". */
export function monthLabel(period: string, language = "es"): string {
  const [y, m] = period.split("-").map(Number);
  if (!y || !m) return period;
  return `${(lang(language) === "en" ? MONTHS_EN : MONTHS_ES)[m - 1]} ${y}`;
}

/** Mes actual (YYYY-MM) en hora de Ciudad de México. */
export function currentMonth(now = new Date()): string {
  return new Date(now.getTime() - 6 * 3600 * 1000).toISOString().slice(0, 7);
}

/** Hoy (YYYY-MM-DD) en hora de Ciudad de México. */
export function currentDay(now = new Date()): string {
  return new Date(now.getTime() - 6 * 3600 * 1000).toISOString().slice(0, 10);
}

export function shiftMonth(period: string, delta: number): string {
  const [y, m] = period.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1 + delta, 1)).toISOString().slice(0, 7);
}

export function lastDayOf(period: string): string {
  const [y, m] = period.split("-").map(Number);
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}

export function downloadText(filename: string, text: string, type = "text/csv;charset=utf-8") {
  const blob = new Blob(["﻿", text], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
